import React, { useState, useEffect, useRef } from "react";
import "./NeuralCockpit.scss";

/**
 * NEURAL COCKPIT - The Future of Gaming Command Centers
 * A living, breathing interface that evolves with the player
 */
const NeuralCockpit: React.FC = () => {
    const [currentGame, setCurrentGame] = useState("apex");
    const [guildPulse, setGuildPulse] = useState(0);
    const [revenueFlow, setRevenueFlow] = useState({ fuel: 1250, xp: 48750 });
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Simulate real-time data flow
    useEffect(() => {
        const interval = setInterval(() => {
            setGuildPulse(prev => (prev + 1) % 100);
            setRevenueFlow(prev => ({
                fuel: prev.fuel + Math.floor(Math.random() * 10),
                xp: prev.xp + Math.floor(Math.random() * 50),
            }));
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    // Neural mesh background animation
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw neural connections
            const time = Date.now() * 0.001;
            ctx.strokeStyle = "rgba(120, 89, 230, 0.1)";
            ctx.beginPath();

            for (let i = 0; i < 5; i++) {
                const x1 = Math.sin(time + i) * 100 + canvas.width * 0.2;
                const y1 = Math.cos(time + i) * 100 + canvas.height * 0.5;
                const x2 = Math.sin(time + i + 1) * 100 + canvas.width * 0.8;
                const y2 = Math.cos(time + i + 1) * 100 + canvas.height * 0.5;

                ctx.moveTo(x1, y1);
                ctx.quadraticCurveTo(canvas.width * 0.5, canvas.height * 0.5, x2, y2);
            }

            ctx.stroke();
            requestAnimationFrame(animate);
        };

        animate();
    }, []);

    return (
        <div className="neural-cockpit">
            <canvas ref={canvasRef} className="neural-mesh" />

            {/* LEFT PANEL: GUILD NEXUS & PERSONAL METRICS */}
            <div className="panel panel--left">
                {/* Guild Nexus - Your Squad's Living Heartbeat */}
                <div className="guild-nexus">
                    <div className="guild-core">
                        <div className="guild-pulse" style={{ "--pulse-progress": guildPulse }}>
                            <div className="pulse-ring" />
                            <div className="pulse-ring pulse-ring--2" />
                            <div className="pulse-ring pulse-ring--3" />
                        </div>
                        <div className="guild-info">
                            <h3 className="guild-name">APEX PREDATORS</h3>
                            <div className="guild-rank">#7 GLOBAL</div>
                            <div className="guild-members">
                                <div className="member-avatar member-avatar--active" />
                                <div className="member-avatar member-avatar--active" />
                                <div className="member-avatar" />
                                <span className="member-count">+12</span>
                            </div>
                        </div>
                    </div>

                    {/* Guild War Status */}
                    <div className="guild-war-status">
                        <div className="war-progress">
                            <div className="war-bar">
                                <div className="war-fill" style={{ width: "67%" }} />
                                <div className="war-marker" />
                            </div>
                            <div className="war-labels">
                                <span>VICTORY IN</span>
                                <span className="war-timer">2H 14M</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Revenue Stream Visualizer */}
                <div className="revenue-stream">
                    <div className="stream-header">
                        <h4>REVENUE FLOW</h4>
                        <div className="stream-live">
                            <span className="live-dot" />
                            LIVE
                        </div>
                    </div>

                    <div className="revenue-channels">
                        <div className="channel channel--fuel">
                            <div className="channel-flow">
                                <div className="flow-particle" />
                                <div className="flow-particle flow-particle--2" />
                                <div className="flow-particle flow-particle--3" />
                            </div>
                            <div className="channel-data">
                                <span className="channel-icon">⚡</span>
                                <span className="channel-value">{revenueFlow.fuel.toLocaleString()}</span>
                                <span className="channel-label">FUEL</span>
                            </div>
                        </div>

                        <div className="channel channel--xp">
                            <div className="channel-flow">
                                <div className="flow-particle" />
                                <div className="flow-particle flow-particle--2" />
                            </div>
                            <div className="channel-data">
                                <span className="channel-icon">✦</span>
                                <span className="channel-value">{revenueFlow.xp.toLocaleString()}</span>
                                <span className="channel-label">XP</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Neural Activity Map */}
                <div className="neural-activity">
                    <h4>PLAYSTYLE DNA</h4>
                    <div className="dna-viz">
                        <svg viewBox="0 0 200 200" className="dna-chart">
                            <polygon
                                points="100,20 170,60 170,140 100,180 30,140 30,60"
                                className="dna-web"
                            />
                            <polygon
                                points="100,50 140,80 140,120 100,150 60,120 60,80"
                                className="dna-stats"
                            />
                        </svg>
                        <div className="dna-labels">
                            <span className="dna-label dna-label--top">AGGRO</span>
                            <span className="dna-label dna-label--right">LEADER</span>
                            <span className="dna-label dna-label--bottom">SUPPORT</span>
                            <span className="dna-label dna-label--left">TACTICIAN</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* CENTER PANEL: QUANTUM QUEST NEXUS */}
            <div className="panel panel--center">
                {/* Game Selector - Dimensional Portal */}
                <div className="dimension-selector">
                    <div className="dimension-orb dimension-orb--active" onClick={() => setCurrentGame("apex")}>
                        <img src="/images/games/apex-logo.png" alt="Apex" />
                        <div className="orb-energy" />
                    </div>
                    <div className="dimension-orb" onClick={() => setCurrentGame("valorant")}>
                        <img src="/images/games/valorant-logo.png" alt="Valorant" />
                    </div>
                    <div className="dimension-orb" onClick={() => setCurrentGame("fortnite")}>
                        <img src="/images/games/fortnite-logo.png" alt="Fortnite" />
                    </div>
                    <div className="dimension-orb dimension-orb--multi">
                        <span className="multi-icon">∞</span>
                    </div>
                </div>

                {/* Featured Brand Journey - Revenue Focused */}
                <div className="brand-journey">
                    <div className="journey-rift">
                        <div className="rift-energy" />
                        <div className="rift-portal" />
                    </div>

                    <div className="journey-content">
                        <div className="journey-sponsor">
                            <img src="/images/brands/redbull-logo.png" alt="Red Bull" />
                            <div className="sponsor-glow" />
                        </div>

                        <h2 className="journey-title">CLUTCH OR KICK CHALLENGE</h2>
                        <p className="journey-desc">5 clutch rounds across any game</p>

                        <div className="journey-rewards">
                            <div className="reward-orb reward-orb--premium">
                                <span className="reward-value">$10</span>
                                <span className="reward-type">FUEL</span>
                            </div>
                            <div className="reward-orb">
                                <span className="reward-value">5K</span>
                                <span className="reward-type">XP</span>
                            </div>
                            <div className="reward-orb reward-orb--rare">
                                <span className="reward-icon">🎁</span>
                                <span className="reward-type">LOOT</span>
                            </div>
                        </div>

                        <div className="journey-participants">
                            <div className="participant-wave" />
                            <span className="participant-count">2,847 HUNTERS ACTIVE</span>
                        </div>

                        <button className="journey-enter">
                            <span className="enter-text">ENTER THE RIFT</span>
                            <div className="enter-energy" />
                        </button>
                    </div>
                </div>

                {/* Quantum Quest Grid */}
                <div className="quantum-grid">
                    {/* Active Game Quest */}
                    <div className="quantum-card quantum-card--active">
                        <div className="card-particles">
                            <div className="particle" />
                            <div className="particle particle--2" />
                            <div className="particle particle--3" />
                        </div>

                        <div className="card-content">
                            <div className="quest-type">APEX LEGENDS</div>
                            <h3 className="quest-title">DAMAGE DEALER</h3>
                            <div className="quest-progress">
                                <div className="progress-ring">
                                    <svg viewBox="0 0 100 100">
                                        <circle cx="50" cy="50" r="45" className="ring-bg" />
                                        <circle cx="50" cy="50" r="45" className="ring-fill"
                                            style={{ strokeDasharray: 283, strokeDashoffset: 85 }} />
                                    </svg>
                                    <span className="progress-text">70%</span>
                                </div>
                            </div>
                            <div className="quest-reward">
                                <span className="reward-amount">500</span>
                                <span className="reward-fuel">⚡</span>
                            </div>
                        </div>

                        <div className="card-glow" />
                    </div>

                    {/* Guild Challenge */}
                    <div className="quantum-card quantum-card--guild">
                        <div className="guild-banner">
                            <span className="banner-icon">⚔️</span>
                            GUILD QUEST
                        </div>
                        <div className="card-content">
                            <h3 className="quest-title">SQUAD WIPE MASTERS</h3>
                            <div className="guild-progress">
                                <div className="contributors">
                                    <img src="/placeholder-avatar.jpg" alt="Contributor" />
                                    <img src="/placeholder-avatar.jpg" alt="Contributor" />
                                    <img src="/placeholder-avatar.jpg" alt="Contributor" />
                                    <span>+9</span>
                                </div>
                                <div className="contribution-bar">
                                    <div className="contribution-fill" style={{ width: "45%" }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Time-Limited Event */}
                    <div className="quantum-card quantum-card--urgent">
                        <div className="urgency-pulse" />
                        <div className="card-content">
                            <div className="timer-warning">
                                <span className="timer-icon">⏰</span>
                                <span className="timer-text">EXPIRES IN 47M</span>
                            </div>
                            <h3 className="quest-title">FLASH QUEST</h3>
                            <div className="quest-requirement">Win 1 match</div>
                            <div className="flash-reward">
                                <span>INSTANT</span>
                                <span className="reward-flash">1000 XP</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT PANEL: SOCIAL PULSE & ACTIVITY STREAM */}
            <div className="panel panel--right">
                {/* Social Connections Hub */}
                <div className="social-nexus">
                    <h4>SQUAD PULSE</h4>
                    <div className="social-rings">
                        <div className="ring-layer ring-layer--1">
                            <div className="social-node social-node--online" />
                            <div className="social-node social-node--online" />
                            <div className="social-node" />
                        </div>
                        <div className="ring-layer ring-layer--2">
                            <div className="social-node" />
                            <div className="social-node social-node--ingame" />
                        </div>
                    </div>
                    <div className="social-stats">
                        <span className="stat">12 ONLINE</span>
                        <span className="stat">5 IN-GAME</span>
                    </div>
                </div>

                {/* Activity Waterfall */}
                <div className="activity-waterfall">
                    <div className="activity-flow">
                        <div className="activity-item activity-item--achievement">
                            <div className="activity-pulse" />
                            <div className="activity-content">
                                <img src="/placeholder-avatar.jpg" alt="Player" className="activity-avatar" />
                                <div className="activity-text">
                                    <span className="player-name">xKiller99</span>
                                    <span className="activity-action">unlocked PREDATOR</span>
                                </div>
                                <div className="activity-time">2m</div>
                            </div>
                        </div>

                        <div className="activity-item activity-item--transaction">
                            <div className="activity-pulse" />
                            <div className="activity-content">
                                <div className="transaction-icon">💎</div>
                                <div className="activity-text">
                                    <span className="transaction-type">LOOTBOX OPENED</span>
                                    <span className="transaction-result">Epic Skin</span>
                                </div>
                            </div>
                        </div>

                        <div className="activity-item activity-item--social">
                            <div className="activity-pulse" />
                            <div className="activity-content">
                                <div className="social-avatars">
                                    <img src="/placeholder-avatar.jpg" alt="Player" />
                                    <img src="/placeholder-avatar.jpg" alt="Player" />
                                </div>
                                <div className="activity-text">
                                    <span className="social-action">Squad formed</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="neural-actions">
                    <button className="action-node action-node--primary">
                        <span className="node-icon">🎮</span>
                        <span className="node-label">FIND MATCH</span>
                        <div className="node-energy" />
                    </button>

                    <button className="action-node action-node--rewards">
                        <span className="node-icon">🎁</span>
                        <span className="node-label">CLAIM (3)</span>
                        <div className="node-pulse" />
                    </button>
                </div>
            </div>

            {/* Ambient Effects */}
            <div className="ambient-effects">
                <div className="scan-line" />
                <div className="energy-field" />
            </div>
        </div>
    );
};

export default NeuralCockpit;